import { DEFAULT_PAGE_SIZE } from "../../component/common/TablePaginator";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import { api } from "../../util/api";
import { notify } from "../../util/toast";

import { approvalApi } from "./api/approvalApi";
import CreateApprovalModal from "./CreateApprovalModal";
import ApprovalDetail      from "./ApprovalDetail";
import ApprovalAdminPanel  from "./ApprovalAdminPanel";
import ApprovalNormalPanel from "./ApprovalNormalPanel";
import DetailModal         from "../../component/common/DetailModal";

const BASE = process.env.REACT_APP_API_REAL_URL;

function extractList(res) {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.content)) return res.content;
    if (Array.isArray(res?.data)) return res.data;
    return [];
}

export default function ApprovalPage() {
    const [tab, setTab]               = useState(0);
    const [loading, setLoading]       = useState(true);
    const [selected, setSelected]     = useState(null);
    const [viewStack, setViewStack]   = useState([]);   // 결재로 이동 시 위에 겹쳐 여는 열람용 모달 스택
    const [createOpen, setCreateOpen] = useState(false);
    const [myInfo, setMyInfo]         = useState(null);
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [submitterFilter, setSubmitterFilter] = useState("");
    const [typeFilter, setTypeFilter]           = useState("ALL");

    const [inboxApprovals, setInboxApprovals]         = useState([]);
    const [completedApprovals, setCompletedApprovals] = useState([]);
    const [sentApprovals, setSentApprovals]           = useState([]);

    const [isAdmin, setIsAdmin]           = useState(false);
    const [adminMode, setAdminMode]       = useState(false);
    const [adminApprovals, setAdminApprovals] = useState([]);
    const [adminLoading, setAdminLoading] = useState(false);
    const [adminTypeFilter, setAdminTypeFilter]     = useState("ALL");
    const [adminStatusFilter, setAdminStatusFilter] = useState("ALL");
    const [adminSubmitterFilter, setAdminSubmitterFilter] = useState("");
    const [adminApproverFilter, setAdminApproverFilter] = useState("");

    // 관리자 탭 서버 페이지네이션 (관리자는 전체 회사 결재 → 많을 수 있으므로 유지)
    const [adminPage, setAdminPage]         = useState(1);
    const [adminPageSize, setAdminPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [adminTotalPages, setAdminTotalPages] = useState(1);
    const [adminTotalElements, setAdminTotalElements] = useState(0);

    /* ── 내 정보 + 권한 ── */
    useEffect(() => {
        const token = localStorage.getItem("auth_token");
        if (!token) return;
        Promise.all([
            api.get(`${BASE}/api/employees/me`),
            api.get(`${BASE}/api/employees/me/permissions`),
        ]).then(([meData, permData]) => {
            setMyInfo(meData);
            const perms = Array.isArray(permData) ? permData : [];
            setIsAdmin(perms.includes("GLOBAL_ADMIN") || perms.includes("GLOBAL_ADMIN"));
        }).catch(() => {
            api.get(`${BASE}/api/employees/me`).then((r) => setMyInfo(r)).catch(() => {});
        });
    }, []);

    /* ── 탭 인덱스 → 해당 탭만 신규 조회 ── */
    const loadTab = async (tabIndex) => {
        setLoading(true);
        try {
            if (tabIndex === 1) {
                const res = await approvalApi.inbox();
                setInboxApprovals(extractList(res));
            } else if (tabIndex === 2) {
                const res = await approvalApi.completed(0, 9999);
                setCompletedApprovals(extractList(res));
            } else if (tabIndex === 3) {
                const res = await approvalApi.sent(0, 9999);
                setSentApprovals(extractList(res));
            } else {
                // 전체 보기(0): 받은·처리완료·올린 모두 로드 후 병합
                const [i, c, s] = await Promise.all([
                    approvalApi.inbox(),
                    approvalApi.completed(0, 9999),
                    approvalApi.sent(0, 9999),
                ]);
                setInboxApprovals(extractList(i));
                setCompletedApprovals(extractList(c));
                setSentApprovals(extractList(s));
            }
        } catch {
            notify("결재 목록 불러오기 실패", "error");
        } finally {
            setLoading(false);
        }
    };

    /* ── 초기 로드 + 사이드바 딥링크(?view=, ?create=)는 아래 location 기반 effect 에서 처리 ── */

    /* ── 전체 탭: 받은+처리완료+올린 병합 후 중복 제거(결재 ID 기준), 최신순 ── */
    const allApprovals = useMemo(() => {
        const map = new Map();
        [...inboxApprovals, ...completedApprovals, ...sentApprovals].forEach((a) => {
            if (a && !map.has(a.id)) map.set(a.id, a);
        });
        return Array.from(map.values())
            .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    }, [inboxApprovals, completedApprovals, sentApprovals]);

    /* ── 탭별 현재 데이터 (0:전체 보기, 1:받은, 2:처리완료, 3:올린) ── */
    const approvals = tab === 1 ? inboxApprovals
        : tab === 2 ? completedApprovals
        : tab === 3 ? sentApprovals
        : allApprovals;

    /* ── 새로고침: 현재 탭만 재조회 ── */
    const refresh = () => loadTab(tab);

    /* ── 삭제 후 전체 일반 탭 갱신 (받은결재·처리완료·올린결재 모두 영향 가능) ── */
    const refreshAll = () => {
        Promise.allSettled([
            approvalApi.inbox().then(r => setInboxApprovals(extractList(r))),
            approvalApi.completed(0, 9999).then(r => setCompletedApprovals(extractList(r))),
            approvalApi.sent(0, 9999).then(r => setSentApprovals(extractList(r))),
        ]);
    };

    /* ── 관리자 목록 로드 ── */
    const loadAdmin = async (p = adminPage) => {
        setAdminLoading(true);
        try {
            const res = await approvalApi.adminAll(adminStatusFilter, adminTypeFilter, p - 1, adminPageSize, adminSubmitterFilter, adminApproverFilter);
            const data = extractList(res);
            setAdminApprovals(data);
            setAdminTotalPages(res?.totalPages ?? 1);
            setAdminTotalElements(res?.totalElements ?? data.length);
        } catch {
            notify("관리자 목록 불러오기 실패", "error");
        } finally {
            setAdminLoading(false);
        }
    };

    useEffect(() => {
        if (adminMode && isAdmin) { setAdminPage(1); loadAdmin(1); }
    }, [adminMode, adminTypeFilter, adminStatusFilter]); // eslint-disable-line

    useEffect(() => {
        if (adminMode && isAdmin) loadAdmin(adminPage);
    }, [adminPage, adminPageSize]); // eslint-disable-line

    // 기안자·결재자 텍스트 검색을 서버로 — 입력 디바운스 후 page 1 부터 재조회.
    // (검색이 서버로 내려가므로 filteredAdminApprovals 의 클라이언트 필터는 현재 페이지 즉시반영용 보조로만 남는다)
    useEffect(() => {
        if (!(adminMode && isAdmin)) return;
        const t = setTimeout(() => { setAdminPage(1); loadAdmin(1); }, 350);
        return () => clearTimeout(t);
    }, [adminSubmitterFilter, adminApproverFilter]); // eslint-disable-line

    const handleSelectApproval = async (approval) => {
        try {
            const data = await approvalApi.detail(approval.id);
            setSelected(data?.data ?? data);
        } catch {
            setSelected(approval);
        }
    };

    /* 결재로 이동 — 현재 모달을 유지한 채 위에 새 열람용 모달을 겹쳐 연다. */
    const pushApprovalView = async (id) => {
        try {
            const data = await approvalApi.detail(id);
            setViewStack((s) => [...s, data?.data ?? data]);
        } catch {
            notify("결재를 불러오지 못했습니다.", "warning");
        }
    };
    /* idx 위치 모달(과 그 위 모달들)을 닫는다. */
    const popApprovalView = (idx) => setViewStack((s) => s.slice(0, idx));
    /* 스택 안 결재 상세 새로고침 (승인/반려 등 처리 후). */
    const reloadApprovalView = async (idx) => {
        const cur = viewStack[idx];
        if (!cur) return;
        try {
            const data = await approvalApi.detail(cur.id);
            setViewStack((s) => s.map((x, i) => (i === idx ? (data?.data ?? data) : x)));
        } catch { /* 무시 */ }
    };

    /* 알람으로부터 진입: ?openApproval=<id> 가 있으면 해당 결재 상세를 자동으로 연다.
       location.search 가 바뀔 때마다 동작 → 페이지에 이미 머물러 있는 상태에서
       다른 알람을 또 클릭해도 정상 처리. 쿼리는 처리 직후 제거해 새로고침 시 재오픈 안 됨.
       (history.replace 직후 effect 가 재실행되지만 ?openApproval 이 없어 일찍 return) */
    const history  = useHistory();
    const location = useLocation();

    /* 초기 로드 + 사이드바 딥링크 동기화:
       ?view=received → 받은 결재(탭1), ?view=sent → 올린 결재(탭3), 그 외 → 전체(탭0).
       ?create=1 이면 결재 작성 모달을 열고 파라미터를 제거(새로고침·재실행 시 재오픈 방지). */
    useEffect(() => {
        const sp = new URLSearchParams(location.search);
        const v = sp.get("view");
        const target = v === "received" ? 1 : v === "sent" ? 3 : 0;
        setTab(target);
        loadTab(target);
        if (sp.get("create") === "1") {
            setCreateOpen(true);
            sp.delete("create");
            history.replace({ pathname: location.pathname, search: sp.toString() ? `?${sp}` : "" });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    useEffect(() => {
        const sp = new URLSearchParams(location.search);
        const id = sp.get("openApproval");
        if (!id) return;
        let cancelled = false;
        (async () => {
            try {
                const data = await approvalApi.detail(Number(id));
                if (!cancelled) setSelected(data?.data ?? data);
            } catch {
                if (!cancelled) notify("결재를 불러오지 못했습니다.", "warning");
            } finally {
                history.replace(location.pathname);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const handleRefresh = async () => {
        refresh();
        if (adminMode && isAdmin) loadAdmin(adminPage);
        // 열려 있는 상세 모달도 최신 상태로 재조회 — 승인/반려/보류 처리 후 모달이 stale 하게 남아
        // 버튼이 재노출되고 다시 누르면 "결재 차례 아님" 오류가 나던 문제 방지.
        if (selected) {
            try {
                const data = await approvalApi.detail(selected.id);
                setSelected(data?.data ?? data);
            } catch { /* 무시 */ }
        }
    };

    /* ── 필터(모든 탭 공통): 상태·결재유형·기안자 ── */
    const filtered = useMemo(() => {
        const sub = submitterFilter.trim().toLowerCase();
        return approvals.filter((a) => {
            if (statusFilter !== "ALL" && a.status !== statusFilter) return false;
            if (typeFilter   !== "ALL" && a.type   !== typeFilter)   return false;
            if (sub) {
                const text = `${a.submitterName || ""} ${a.submitterDept || ""} ${a.submitterPosition || ""}`.toLowerCase();
                if (!text.includes(sub)) return false;
            }
            return true;
        });
    }, [approvals, statusFilter, typeFilter, submitterFilter]);

    const filteredAdminApprovals = useMemo(() => {
        const submitter = adminSubmitterFilter.trim().toLowerCase();
        const approver  = adminApproverFilter.trim().toLowerCase();

        return adminApprovals.filter((a) => {
            const submitterText = `${a.submitterName || ""} ${a.submitterDept || ""} ${a.submitterPosition || ""}`.toLowerCase();
            const approverText  = (a.lines || [])
                .map((l) => `${l.approverName || ""} ${l.approverDept || ""} ${l.approverPosition || ""}`)
                .join(" ").toLowerCase();

            if (submitter && !submitterText.includes(submitter)) return false;
            if (approver  && !approverText.includes(approver))   return false;
            return true;
        });
    }, [adminApprovals, adminSubmitterFilter, adminApproverFilter]);

    return (
        <Box sx={{ height: "100%", bgcolor: "#fff", overflowY: "auto" }}>
            <Box sx={{ p: 2.5 }}>
                {adminMode ? (
                    <ApprovalAdminPanel
                        adminApprovals={filteredAdminApprovals}
                        totalCount={adminTotalElements}
                        adminLoading={adminLoading}
                        adminTypeFilter={adminTypeFilter}
                        setAdminTypeFilter={setAdminTypeFilter}
                        adminStatusFilter={adminStatusFilter}
                        setAdminStatusFilter={setAdminStatusFilter}
                        adminSubmitterFilter={adminSubmitterFilter}
                        setAdminSubmitterFilter={setAdminSubmitterFilter}
                        adminApproverFilter={adminApproverFilter}
                        setAdminApproverFilter={setAdminApproverFilter}
                        onExit={() => { setAdminMode(false); setSelected(null); }}
                        onRefresh={() => loadAdmin(adminPage)}
                        selected={selected}
                        onSelect={handleSelectApproval}
                        page={adminPage}
                        pageSize={adminPageSize}
                        totalPages={adminTotalPages}
                        totalElements={adminTotalElements}
                        onPageChange={setAdminPage}
                        onPageSizeChange={v => { setAdminPageSize(v); setAdminPage(1); }}
                    />
                ) : (
                    <ApprovalNormalPanel
                        tab={tab}
                        setTab={(v) => { setTab(v); setStatusFilter("ALL"); setSelected(null); loadTab(v); }}
                        approvals={approvals}
                        loading={loading}
                        filtered={filtered}
                        statusFilter={statusFilter}
                        setStatusFilter={setStatusFilter}
                        submitterFilter={submitterFilter}
                        setSubmitterFilter={setSubmitterFilter}
                        typeFilter={typeFilter}
                        setTypeFilter={setTypeFilter}
                        selected={selected}
                        onSelect={handleSelectApproval}
                        isAdmin={isAdmin}
                        onEnterAdmin={() => { setAdminMode(true); setSelected(null); }}
                        onOpenCreate={() => setCreateOpen(true)}
                        myId={myInfo?.id}
                    />
                )}
            </Box>

            {/* 행 클릭 시 결재 상세 모달 (관리자/정상 공통) */}
            <DetailModal
                open={Boolean(selected)}
                onClose={() => { setSelected(null); setViewStack([]); }}
                title="결재 상세"
                maxWidth="lg"
            >
                {selected && (
                    <ApprovalDetail
                        key={selected.id}
                        approval={selected}
                        myId={myInfo?.id}
                        adminMode={adminMode}
                        onRefresh={handleRefresh}
                        onDelete={() => { setSelected(null); refreshAll(); }}
                        onOpenApproval={pushApprovalView}
                    />
                )}
            </DetailModal>

            {/* 결재로 이동 — 이전 모달 위에 겹쳐 여는 열람용 모달 스택 */}
            {viewStack.map((appr, idx) => (
                <DetailModal
                    key={`stack-${idx}-${appr.id}`}
                    open
                    onClose={() => popApprovalView(idx)}
                    title="결재 상세 (연결된 결재)"
                    maxWidth="lg"
                >
                    <ApprovalDetail
                        key={appr.id}
                        approval={appr}
                        myId={myInfo?.id}
                        adminMode={adminMode}
                        onRefresh={() => reloadApprovalView(idx)}
                        onDelete={() => popApprovalView(idx)}
                        onOpenApproval={pushApprovalView}
                    />
                </DetailModal>
            ))}

            <CreateApprovalModal
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={() => {
                    setTab(3);
                    setStatusFilter("ALL");
                    setSelected(null);
                    loadTab(3);
                }}
                myInfo={myInfo}
            />
        </Box>
    );
}
